var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip, FormControl, InputLabel, Select, MenuItem, Chip, OutlinedInput, Alert, CircularProgress, } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, AdminPanelSettings as AdminIcon, Key as KeyIcon, } from '@mui/icons-material';
import { businessService } from '@services/businessService';
const UserManagement = ({ users, loading, onRefresh, onCreateUser, onUpdateUser, onDeleteUser, onUpdateUserRoles, }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [businesses, setBusinesses] = useState([]);
    const [loadingBusinesses, setLoadingBusinesses] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        roles: ['User'],
        defaultBusinessId: null,
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const handleOpenDialog = (user) => __awaiter(void 0, void 0, void 0, function* () {
        // Load businesses
        try {
            setLoadingBusinesses(true);
            const data = yield businessService.getAll();
            setBusinesses(data);
        }
        catch (error) {
            console.error('Error loading businesses:', error);
        }
        finally {
            setLoadingBusinesses(false);
        }
        if (user) {
            setSelectedUser(user);
            setFormData({
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                password: '',
                roles: user.roles || ['User'],
                defaultBusinessId: user.defaultBusinessId || null,
            });
        }
        else {
            setSelectedUser(null);
            setFormData({
                email: '',
                firstName: '',
                lastName: '',
                password: '',
                roles: ['User'],
                defaultBusinessId: null,
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    });
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
        setFormData({
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            roles: ['User'],
            defaultBusinessId: null,
        });
        setFormErrors({});
    };
    const validateForm = () => {
        const errors = {};
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email format';
        }
        if (!formData.firstName.trim()) {
            errors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            errors.lastName = 'Last name is required';
        }
        if (!selectedUser && !formData.password) {
            errors.password = 'Password is required for new users';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!validateForm())
            return;
        setSubmitting(true);
        try {
            const userData = Object.assign({ email: formData.email, firstName: formData.firstName, lastName: formData.lastName, roles: formData.roles, defaultBusinessId: (_a = formData.defaultBusinessId) !== null && _a !== void 0 ? _a : undefined }, (formData.password && { password: formData.password }));
            if (selectedUser) {
                yield onUpdateUser(selectedUser.id, userData);
            }
            else {
                yield onCreateUser(userData);
            }
            handleCloseDialog();
            onRefresh();
        }
        catch (error) {
            console.error('Error saving user:', error);
        }
        finally {
            setSubmitting(false);
        }
    });
    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setDeleteDialogOpen(true);
    };
    const handlePasswordClick = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setPasswordDialogOpen(true);
    };
    const handlePasswordChange = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedUser || !newPassword)
            return;
        try {
            setSubmitting(true);
            const { userService } = yield import('@services/userService');
            yield userService.adminChangePassword(selectedUser.id, newPassword);
            setPasswordDialogOpen(false);
            setNewPassword('');
            setSelectedUser(null);
            onRefresh();
        }
        catch (error) {
            console.error('Error changing password:', error);
        }
        finally {
            setSubmitting(false);
        }
    });
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedUser)
            return;
        setSubmitting(true);
        try {
            yield onDeleteUser(selectedUser.id);
            setDeleteDialogOpen(false);
            setSelectedUser(null);
            onRefresh();
        }
        catch (error) {
            console.error('Error deleting user:', error);
        }
        finally {
            setSubmitting(false);
        }
    });
    const handleRoleChange = (user, role) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const currentRoles = user.roles || [];
            let newRoles;
            if (currentRoles.includes(role)) {
                // Remove role if it exists
                newRoles = currentRoles.filter(r => r !== role);
                // Ensure user has at least 'User' role
                if (newRoles.length === 0) {
                    newRoles = ['User'];
                }
            }
            else {
                // Add role if it doesn't exist
                newRoles = [...currentRoles, role];
            }
            yield onUpdateUserRoles(user.id, newRoles);
            onRefresh();
        }
        catch (error) {
            console.error('Error updating user roles:', error);
        }
    });
    const columns = [
        {
            field: 'email',
            headerName: 'Email',
            flex: 1,
            minWidth: 200
        },
        {
            field: 'firstName',
            headerName: 'First Name',
            width: 150
        },
        {
            field: 'lastName',
            headerName: 'Last Name',
            width: 150
        },
        {
            field: 'roles',
            headerName: 'Roles',
            width: 200,
            renderCell: (params) => {
                var _a, _b;
                return (_jsx(Tooltip, { title: ((_a = params.value) === null || _a === void 0 ? void 0 : _a.join(', ')) || 'No roles', children: _jsx("span", { children: ((_b = params.value) === null || _b === void 0 ? void 0 : _b.join(', ')) || '—' }) }));
            },
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 130,
            valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '',
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 150,
            getActions: (params) => [
                _jsx(GridActionsCellItem, { icon: _jsx(Tooltip, { title: (params.row.roles || []).includes('Admin') ? 'Remove Admin Role' : 'Add Admin Role', children: _jsx(AdminIcon, { color: (params.row.roles || []).includes('Admin') ? 'primary' : 'disabled' }) }), label: "Toggle Admin", onClick: () => handleRoleChange(params.row, 'Admin') }),
                _jsx(GridActionsCellItem, { icon: _jsx(EditIcon, {}), label: "Edit", onClick: () => handleOpenDialog(params.row) }),
                _jsx(GridActionsCellItem, { icon: _jsx(KeyIcon, {}), label: "Change Password", onClick: () => handlePasswordClick(params.row), showInMenu: true }),
                _jsx(GridActionsCellItem, { icon: _jsx(DeleteIcon, {}), label: "Delete", onClick: () => handleDeleteClick(params.row), showInMenu: true }),
            ],
        },
    ];
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }, children: [_jsx(Typography, { variant: "h6", children: "User Management" }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: () => handleOpenDialog(), children: "Add User" })] }), loading ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 4 }, children: _jsx(CircularProgress, {}) })) : (_jsx(Box, { sx: { height: 500, width: '100%' }, children: _jsx(DataGrid, { rows: users, columns: columns, pageSizeOptions: [10, 25, 50], initialState: {
                        pagination: {
                            paginationModel: { pageSize: 25 },
                        },
                    }, disableRowSelectionOnClick: true }) })), _jsxs(Dialog, { open: openDialog, onClose: handleCloseDialog, maxWidth: "sm", fullWidth: true, children: [_jsx(DialogTitle, { children: selectedUser ? 'Edit User' : 'Create New User' }), _jsx(DialogContent, { children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }, children: [_jsx(TextField, { label: "Email", type: "email", value: formData.email, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { email: e.target.value })), error: !!formErrors.email, helperText: formErrors.email, fullWidth: true, required: true }), _jsx(TextField, { label: "First Name", value: formData.firstName, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { firstName: e.target.value })), error: !!formErrors.firstName, helperText: formErrors.firstName, fullWidth: true, required: true }), _jsx(TextField, { label: "Last Name", value: formData.lastName, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { lastName: e.target.value })), error: !!formErrors.lastName, helperText: formErrors.lastName, fullWidth: true, required: true }), _jsx(TextField, { label: "Password", type: "password", value: formData.password, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { password: e.target.value })), error: !!formErrors.password, helperText: formErrors.password || (selectedUser ? 'Leave blank to keep current password' : ''), fullWidth: true, required: !selectedUser }), _jsxs(FormControl, { fullWidth: true, children: [_jsx(InputLabel, { children: "Roles" }), _jsxs(Select, { multiple: true, value: formData.roles, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { roles: e.target.value })), input: _jsx(OutlinedInput, { label: "Roles" }), renderValue: (selected) => (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 0.5 }, children: selected.map((value) => (_jsx(Chip, { label: value, size: "small" }, value))) })), children: [_jsx(MenuItem, { value: "User", children: "User" }), _jsx(MenuItem, { value: "Admin", children: "Admin" }), _jsx(MenuItem, { value: "Owner", children: "Owner" })] })] }), _jsxs(FormControl, { fullWidth: true, children: [_jsx(InputLabel, { children: "Default Business" }), _jsxs(Select, { value: formData.defaultBusinessId || '', onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { defaultBusinessId: e.target.value })), label: "Default Business", disabled: loadingBusinesses, children: [_jsx(MenuItem, { value: "", children: _jsx("em", { children: "None" }) }), businesses.map((business) => (_jsx(MenuItem, { value: business.id, children: business.name }, business.id)))] })] })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleCloseDialog, disabled: submitting, children: "Cancel" }), _jsx(Button, { onClick: handleSubmit, variant: "contained", disabled: submitting, children: submitting ? 'Saving...' : selectedUser ? 'Update' : 'Create' })] })] }), _jsxs(Dialog, { open: deleteDialogOpen, onClose: () => setDeleteDialogOpen(false), children: [_jsx(DialogTitle, { children: "Confirm Delete" }), _jsx(DialogContent, { children: _jsxs(Alert, { severity: "warning", sx: { mb: 2 }, children: ["Are you sure you want to delete user ", _jsx("strong", { children: selectedUser === null || selectedUser === void 0 ? void 0 : selectedUser.email }), "? This action cannot be undone."] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setDeleteDialogOpen(false), disabled: submitting, children: "Cancel" }), _jsx(Button, { onClick: handleDeleteConfirm, color: "error", variant: "contained", disabled: submitting, children: submitting ? 'Deleting...' : 'Delete' })] })] }), _jsxs(Dialog, { open: passwordDialogOpen, onClose: () => setPasswordDialogOpen(false), maxWidth: "sm", fullWidth: true, children: [_jsx(DialogTitle, { children: "Change Password" }), _jsxs(DialogContent, { children: [_jsxs(Alert, { severity: "info", sx: { mb: 2 }, children: ["Changing password for user: ", _jsx("strong", { children: selectedUser === null || selectedUser === void 0 ? void 0 : selectedUser.email })] }), _jsx(TextField, { autoFocus: true, label: "New Password", type: "password", value: newPassword, onChange: (e) => setNewPassword(e.target.value), fullWidth: true, margin: "normal", required: true, helperText: "Enter a new password for this user" })] }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setPasswordDialogOpen(false), disabled: submitting, children: "Cancel" }), _jsx(Button, { onClick: handlePasswordChange, variant: "contained", disabled: submitting || !newPassword, children: submitting ? 'Changing...' : 'Change Password' })] })] })] }));
};
export default UserManagement;
